import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Tags, UserCog } from 'lucide-react';
import { IconMessageReply } from '@tabler/icons-react';
import { Ci2HelpHexIcon } from './Ci2HelpHexIcon';
import '@fontsource-variable/geist/wght.css';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { hasGoAppLicense } from '../../../utils/userFinanceiroAccess';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { GoDevicesDisplayMenu } from '../../go-config/components/GoDevicesDisplayMenu';
import '../ConfigStatusView2.css';
import ProcessConfigSkeleton from '../ConfigStatusView2Skeleton';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../users-customers/components/UsersCustomersView.css';
import '../../equipe2/components/Equipe2View.css';
import '../../go-config/components/GoDispositivosConfig.css';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import './CondicoesIntervencaoConfig.css';
import {
  TOOLTIP_ATUALIZA_SLA,
  CAMPOS_CONFIG,
  CAMPOS_GRUPO_KEYS,
  CAMPOS_INTERV_PREFIX,
  CAMPOS_INTERV_SUFFIX,
  COND_INTERV_COL_TOGGLE_KEYS,
  COND_INTERV_COL_LABELS,
  COND_TIPO_OS_FIELDS,
  normCondOsFlag,
  sanitizeMotivoDraft,
  rowTemConfigDefeito,
  emptyGrupoValues,
  getSemPendenciaTipoOsIssues,
  formatSemPendenciaTipoOsBlockMessage,
  rowHasSemPendencia,
  normalizeStockValidationScope,
  sanitizeAlnumCode,
  isExactAlnumCode,
} from './condicoesIntervShared';
import { CondicoesIntervencaoTableV2 } from './CondicoesIntervencaoTableV2';
import { CondicoesIntervNativeConditionModal } from './CondicoesIntervNativeConditionModal';
import { fetchAnchorTechnician, saveAnchorTechnician } from '../services/anchorTechnicianService';

const API_BASE_URL = getApiBaseUrl();
const DEFEITO_RETORNO_INFO =
  'Quando uma ordem de serviço é fechada e gera outra de retorno, o retorno é aberto com o defeito e motivo já pré-configurado';
const SERVICO_RETORNO_INFO =
  'Quando uma ordem de serviço é fechada e gera outra de retorno, o retorno é aberto com o serviço e motivo já pré-configurado';

function Pcs2TooltipHost({ text, children, className = '', align = 'start' }) {
  if (!text) return children;
  return (
    <span
      className={[
        'pcs2-tooltip-host',
        align === 'end' ? 'pcs2-tooltip-host--align-end' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-tooltip={text}
    >
      {children}
    </span>
  );
}

function Pcs2InfoTip({ text }) {
  return (
    <Pcs2TooltipHost text={text} className="pcs2-info-tip-host">
      <Ci2HelpHexIcon label="Mais informações" />
    </Pcs2TooltipHost>
  );
}

function Pcs2Switch({ checked, onChange, ariaLabel, disabled }) {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    onChange(e);
    requestAnimationFrame(() => inputRef.current?.blur());
  };

  return (
    <label className="pcs2-switch">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <span className="pcs2-switch-pill">
        <span className="pcs2-switch-dot" />
      </span>
    </label>
  );
}

function Pcs2SettingRow({ title, desc, descInfo, children }) {
  return (
    <div className="pcs2-setting-row">
      <div className="pcs2-setting-copy">
        <span className="pcs2-setting-title">{title}</span>
        {desc ? (
          <span className="pcs2-setting-desc-row">
            <span className="pcs2-setting-desc">{desc}</span>
            {descInfo ? <Pcs2InfoTip text={descInfo} /> : null}
          </span>
        ) : null}
      </div>
      <div className="pcs2-setting-actions">{children}</div>
    </div>
  );
}

function Pcs2Subblock({ title, desc, hideHead, action, children }) {
  return (
    <div className="pcs2-subblock">
      {!hideHead && (title || desc || action) ? (
        <div className={`pcs2-subhead${action ? ' pcs2-subhead--with-action' : ''}`}>
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
          {action ? <div className="pcs2-subhead-action">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

function Pcs2Section({ icon: Icon, title, desc, children, tablerIcon = false, className = '' }) {
  return (
    <section className={['pcs2-section', className].filter(Boolean).join(' ')}>
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          {tablerIcon ? (
            <Icon size={16} stroke={1.75} aria-hidden />
          ) : (
            <Icon size={16} strokeWidth={1.75} aria-hidden />
          )}
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        <p className="pcs2-section-desc">{desc}</p>
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function CondicoesIntervToolbar({
  searchQuery,
  onSearchChange,
  sortBy,
  sortDir,
  onSortChange,
  onSortDirChange,
  isIntervColVis,
  onToggleCol,
  onRestoreCols,
  children,
}) {
  const sortItems = useMemo(
    () => CONDICAO_SORT_OPTIONS.map((o) => ({ value: o.key, label: o.label })),
    []
  );
  const colItems = useMemo(
    () => COND_INTERV_COL_TOGGLE_KEYS.map((k) => ({ value: k, label: COND_INTERV_COL_LABELS[k] })),
    []
  );
  const colsCustomized = useMemo(() => {
    const defaults = fullVisibilityState();
    return colItems.some((item) => isIntervColVis(item.value) !== defaults[item.value]);
  }, [colItems, isIntervColVis]);

  return (
    <div className="condicoes-interv-toolbar">
      <BoardToolbarExpandSearch
        value={searchQuery}
        onChange={onSearchChange}
        placeholder="Pesquisar condições..."
        ariaLabel="Pesquisar condições"
      />
      <GoDevicesDisplayMenu
        sortItems={sortItems}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={onSortChange}
        onSortDirChange={onSortDirChange}
        colItems={colItems}
        isColVis={isIntervColVis}
        onToggleCol={onToggleCol}
        onRestoreCols={onRestoreCols}
        colsCustomized={colsCustomized}
      />
      {children}
    </div>
  );
}

const fullVisibilityState = () =>
  COND_INTERV_COL_TOGGLE_KEYS.reduce((acc, k) => {
    acc[k] = k === 'atualiza_sla' || k === 'codigo' || k === 'func_counters' ? false : true;
    return acc;
  }, {});

function getCondicaoSortValue(row, key) {
  switch (key) {
    case 'codigo':
      return String(row.TB01055_CODIGO ?? '');
    case 'nome':
      return String(row.TB01055_NOME ?? '');
    case 'defeito':
      return rowTemConfigDefeito(row) ? '1' : '0';
    case 'atualiza_sla':
      return row?.update_sla === 1 || row?.update_sla === true || String(row?.update_sla) === '1' ? '1' : '0';
    case 'func_nao_liga':
      return row?.func_nao_liga === 1 || row?.func_nao_liga === true || String(row?.func_nao_liga) === '1' ? '1' : '0';
    case 'func_sem_pendencia':
      return row?.func_sem_pendencia === 1 || row?.func_sem_pendencia === true || String(row?.func_sem_pendencia) === '1' ? '1' : '0';
    case 'tipo_os':
      return String(COND_TIPO_OS_FIELDS.reduce((acc, f) => acc + normCondOsFlag(row[f.db]), 0));
    case 'permite_fechar':
      return row.func_close === 1 ? '1' : '0';
    default:
      return String(row[key] ?? '');
  }
}

function buildCondicaoSortOptions() {
  return [
    { key: 'nome', label: 'Condição' },
    ...COND_INTERV_COL_TOGGLE_KEYS.map((k) => ({ key: k, label: COND_INTERV_COL_LABELS[k] })),
    { key: 'permite_fechar', label: 'Permite fechar' },
  ];
}

const CONDICAO_SORT_OPTIONS = buildCondicaoSortOptions();

export default function CondicoesIntervencaoConfig() {
  const [condicoesList, setCondicoesList] = useState([]);
  const [isLoadingCondicoes, setIsLoadingCondicoes] = useState(false);
  const [enablingCondicaoId, setEnablingCondicaoId] = useState(null);
  const [enableCondicaoFeedback, setEnableCondicaoFeedback] = useState(null);
  const [disablingCondicaoId, setDisablingCondicaoId] = useState(null);
  const [disableCondicaoFeedback, setDisableCondicaoFeedback] = useState(null);
  const [desabilitarOpenId, setDesabilitarOpenId] = useState(null);
  const [condicaoEditing, setCondicaoEditing] = useState(null);
  const [condicaoEditValue, setCondicaoEditValue] = useState('');
  const [condicaoEditValues, setCondicaoEditValues] = useState(emptyGrupoValues);
  const [condicaoSaving, setCondicaoSaving] = useState(null);
  const [condicaoSuccessAnim, setCondicaoSuccessAnim] = useState(null);
  const [pecaDispHelpOpen, setPecaDispHelpOpen] = useState(false);
  const pecaDispHeaderRef = useRef(null);
  const [atualizaSlaHelpOpen, setAtualizaSlaHelpOpen] = useState(false);
  const atualizaSlaHeaderRef = useRef(null);
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const installHeaderRef = useRef(null);
  const [uninstallHelpOpen, setUninstallHelpOpen] = useState(false);
  const uninstallHeaderRef = useRef(null);
  const [naoLigaHelpOpen, setNaoLigaHelpOpen] = useState(false);
  const naoLigaHeaderRef = useRef(null);
  const [semPendenciaHelpOpen, setSemPendenciaHelpOpen] = useState(false);
  const semPendenciaHeaderRef = useRef(null);
  const [tipoOsHelpOpen, setTipoOsHelpOpen] = useState(false);
  const tipoOsHeaderRef = useRef(null);
  const [tipoOsOpenCod, setTipoOsOpenCod] = useState(null);
  const hasGo = useMemo(() => {
    try {
      return hasGoAppLicense(JSON.parse(localStorage.getItem('userData') || 'null'));
    } catch {
      return false;
    }
  }, []);
  const [defeitoExpandId, setDefeitoExpandId] = useState(null);
  const [defeitoDdOpenCod, setDefeitoDdOpenCod] = useState(null);
  const [defeitoDraftCodbug, setDefeitoDraftCodbug] = useState('');
  const [defeitoDraftReason, setDefeitoDraftReason] = useState('');
  const [defeitoDraftBlank, setDefeitoDraftBlank] = useState(false);
  const [defeitoDraftAsk, setDefeitoDraftAsk] = useState(false);
  const [regBugsList, setRegBugsList] = useState([]);
  const [regBugsLoading, setRegBugsLoading] = useState(false);
  const [defeitoSavingId, setDefeitoSavingId] = useState(null);
  const [defeitoMotivoHelpOpen, setDefeitoMotivoHelpOpen] = useState(false);
  const defeitoMotivoHeaderRef = useRef(null);
  const [defeitoFlagLoaded, setDefeitoFlagLoaded] = useState(false);
  const [defeitoRetornoEnabled, setDefeitoRetornoEnabled] = useState(false);
  const [defeitoFlagSaving, setDefeitoFlagSaving] = useState(false);
  const [colIntervVisibility, setColIntervVisibility] = useState(fullVisibilityState);
  const [reserveSavingKey, setReserveSavingKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('nome');
  const [sortDir, setSortDir] = useState('asc');
  const [nativeMode, setNativeMode] = useState(false);
  const [erpTransition, setErpTransition] = useState(false);
  const [statusTab, setStatusTab] = useState('ativas');
  const [conditionForm, setConditionForm] = useState(null);
  const { error: toastError, success: toastSuccess, Toaster } = useCxExpToast();
  const [anchorTech, setAnchorTech] = useState({ loaded: false, saved: '', value: '', name: null });
  const [anchorSaving, setAnchorSaving] = useState(false);

  const isIntervColVis = useCallback((key) => colIntervVisibility[key] !== false, [colIntervVisibility]);

  const hasOtherNaoLiga = useCallback(
    (exceptCodigo) =>
      condicoesList.some(
        (r) =>
          String(r.TB01055_CODIGO) !== String(exceptCodigo) &&
          (r.func_nao_liga === 1 || r.func_nao_liga === true)
      ),
    [condicoesList]
  );

  const hasOtherSemPendencia = useCallback(
    (exceptCodigo) =>
      condicoesList.some(
        (r) =>
          String(r.TB01055_CODIGO) !== String(exceptCodigo) &&
          (r.func_sem_pendencia === 1 || r.func_sem_pendencia === true)
      ),
    [condicoesList]
  );

  const hasOtherInstalaEquip = useCallback(
    (exceptCodigo) =>
      condicoesList.some(
        (r) =>
          String(r.TB01055_CODIGO) !== String(exceptCodigo) &&
          (r.func_install === 1 || r.func_install === true || String(r.func_install) === '1')
      ),
    [condicoesList]
  );

  const hasOtherDesinstalaEquip = useCallback(
    (exceptCodigo) =>
      condicoesList.some(
        (r) =>
          String(r.TB01055_CODIGO) !== String(exceptCodigo) &&
          (r.func_uninstall === 1 ||
            r.func_uninstall === true ||
            String(r.func_uninstall) === '1')
      ),
    [condicoesList]
  );

  const colSpanDefeitoExpand = 1
    + COND_INTERV_COL_TOGGLE_KEYS.filter((k) => isIntervColVis(k)).length
    + 1;

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/native/status`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then((res) => res.json())
      .then((j) => {
        setNativeMode(!!j.native);
        setErpTransition(!!j.native && !!j.erpTransition);
      })
      .catch(() => setNativeMode(false));
  }, []);

  useEffect(() => {
    if (!nativeMode) return;
    let cancelled = false;
    fetchAnchorTechnician()
      .then(({ data }) => {
        if (cancelled) return;
        const code = data?.code || '';
        setAnchorTech({ loaded: true, saved: code, value: code, name: data?.name || null });
      })
      .catch(() => {
        if (!cancelled) setAnchorTech((prev) => ({ ...prev, loaded: true }));
      });
    return () => { cancelled = true; };
  }, [nativeMode]);

  const anchorDirty = anchorTech.value !== anchorTech.saved;
  const anchorValid = anchorTech.value === '' || /^\d{4}$/.test(anchorTech.value);

  const saveAnchorTech = async () => {
    if (!anchorDirty || !anchorValid) return;
    setAnchorSaving(true);
    try {
      const data = await saveAnchorTechnician(anchorTech.value);
      const code = data?.code || '';
      setAnchorTech({ loaded: true, saved: code, value: code, name: data?.name || null });
      toastSuccess(code ? `Técnico âncora ${code} salvo.` : 'Técnico âncora removido.');
    } catch (e) {
      toastError(e.message);
    } finally {
      setAnchorSaving(false);
    }
  };

  const loadCondicoesIntervencao = useCallback(async () => {
    try {
      setIsLoadingCondicoes(true);
      const token = localStorage.getItem('authToken');
      const qs = `?includeNative=1${nativeMode ? '&includeInactive=1' : ''}`;
      const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao${qs}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const result = await res.json();
      if (result.success && result.data) setCondicoesList(result.data);
      else setCondicoesList([]);
    } catch (e) {
      setCondicoesList([]);
    } finally {
      setIsLoadingCondicoes(false);
    }
  }, [nativeMode]);

  useEffect(() => {
    loadCondicoesIntervencao();
  }, [loadCondicoesIntervencao]);

  const openEditCondition = useCallback((cod) => setConditionForm({ mode: 'edit', codigo: cod }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/defeito-retorno-flag`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const result = await res.json();
        if (cancelled) return;
        if (result.success && result.data) setDefeitoRetornoEnabled(!!result.data.enabled);
        else setDefeitoRetornoEnabled(false);
      } catch {
        if (!cancelled) setDefeitoRetornoEnabled(false);
      } finally {
        if (!cancelled) setDefeitoFlagLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleDefeitoRetornoFlag = async (checked) => {
    setDefeitoFlagSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/defeito-retorno-flag`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: checked })
      });
      const result = await res.json();
      if (result.success && result.data) {
        setDefeitoRetornoEnabled(!!result.data.enabled);
      } else {
        toastError(result.message || 'Erro ao salvar');
      }
    } catch {
      toastError('Erro de conexão');
    } finally {
      setDefeitoFlagSaving(false);
    }
  };

  useEffect(() => {
    if (defeitoFlagLoaded && !defeitoRetornoEnabled) {
      setDefeitoExpandId(null);
      setDefeitoDraftCodbug('');
      setDefeitoDraftReason('');
      setDefeitoDraftBlank(false);
      setDefeitoDraftAsk(false);
      setDefeitoMotivoHelpOpen(false);
    }
  }, [defeitoFlagLoaded, defeitoRetornoEnabled]);

  useEffect(() => {
    setDefeitoDdOpenCod(null);
  }, [defeitoExpandId]);

  const closePecaDispHelp = useCallback((e) => {
    const el = pecaDispHeaderRef.current;
    if (!el || !e?.target) return;
    if (!el.contains(e.target)) setPecaDispHelpOpen(false);
  }, []);

  useEffect(() => {
    if (!pecaDispHelpOpen) return;
    document.addEventListener('mousedown', closePecaDispHelp);
    return () => document.removeEventListener('mousedown', closePecaDispHelp);
  }, [pecaDispHelpOpen, closePecaDispHelp]);

  const closeAtualizaSlaHelp = useCallback((e) => {
    const el = atualizaSlaHeaderRef.current;
    if (!el || !e?.target) return;
    if (!el.contains(e.target)) setAtualizaSlaHelpOpen(false);
  }, []);

  useEffect(() => {
    if (!atualizaSlaHelpOpen) return;
    document.addEventListener('mousedown', closeAtualizaSlaHelp);
    return () => document.removeEventListener('mousedown', closeAtualizaSlaHelp);
  }, [atualizaSlaHelpOpen, closeAtualizaSlaHelp]);

  const closeInstallHelp = useCallback((e) => {
    const el = installHeaderRef.current;
    if (!el || !e?.target) return;
    if (!el.contains(e.target)) setInstallHelpOpen(false);
  }, []);

  useEffect(() => {
    if (!installHelpOpen) return;
    document.addEventListener('mousedown', closeInstallHelp);
    return () => document.removeEventListener('mousedown', closeInstallHelp);
  }, [installHelpOpen, closeInstallHelp]);

  const closeUninstallHelp = useCallback((e) => {
    const el = uninstallHeaderRef.current;
    if (!el || !e?.target) return;
    if (!el.contains(e.target)) setUninstallHelpOpen(false);
  }, []);

  useEffect(() => {
    if (!uninstallHelpOpen) return;
    document.addEventListener('mousedown', closeUninstallHelp);
    return () => document.removeEventListener('mousedown', closeUninstallHelp);
  }, [uninstallHelpOpen, closeUninstallHelp]);

  const closeNaoLigaHelp = useCallback((e) => {
    const el = naoLigaHeaderRef.current;
    if (!el || !e?.target) return;
    if (!el.contains(e.target)) setNaoLigaHelpOpen(false);
  }, []);

  useEffect(() => {
    if (!naoLigaHelpOpen) return;
    document.addEventListener('mousedown', closeNaoLigaHelp);
    return () => document.removeEventListener('mousedown', closeNaoLigaHelp);
  }, [naoLigaHelpOpen, closeNaoLigaHelp]);

  const closeSemPendenciaHelp = useCallback((e) => {
    const el = semPendenciaHeaderRef.current;
    if (!el || !e?.target) return;
    if (!el.contains(e.target)) setSemPendenciaHelpOpen(false);
  }, []);

  useEffect(() => {
    if (!semPendenciaHelpOpen) return;
    document.addEventListener('mousedown', closeSemPendenciaHelp);
    return () => document.removeEventListener('mousedown', closeSemPendenciaHelp);
  }, [semPendenciaHelpOpen, closeSemPendenciaHelp]);

  const closeTipoOsHelp = useCallback((e) => {
    const el = tipoOsHeaderRef.current;
    if (!el || !e?.target) return;
    if (!el.contains(e.target)) setTipoOsHelpOpen(false);
  }, []);

  useEffect(() => {
    if (!tipoOsHelpOpen) return;
    document.addEventListener('mousedown', closeTipoOsHelp);
    return () => document.removeEventListener('mousedown', closeTipoOsHelp);
  }, [tipoOsHelpOpen, closeTipoOsHelp]);

  const closeDefeitoMotivoHelp = useCallback((e) => {
    const el = defeitoMotivoHeaderRef.current;
    if (!el || !e?.target) return;
    if (!el.contains(e.target)) setDefeitoMotivoHelpOpen(false);
  }, []);

  useEffect(() => {
    if (!defeitoMotivoHelpOpen) return;
    document.addEventListener('mousedown', closeDefeitoMotivoHelp);
    return () => document.removeEventListener('mousedown', closeDefeitoMotivoHelp);
  }, [defeitoMotivoHelpOpen, closeDefeitoMotivoHelp]);

  const habilitarCondicao = async (codigo, nome) => {
    if (erpTransition && /^N\d+$/i.test(String(codigo || ''))) {
      toastError('Enquanto ainda houver OS do ERP em aberto, esta condição não pode ser habilitada. Ela existe só no painel e não fecha essas OS. Quando essas OS terminarem, a habilitação volta a ficar disponível.');
      return;
    }
    setEnablingCondicaoId(codigo);
    setEnableCondicaoFeedback(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/habilitar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ codCondicao: codigo, nomeCondicao: nome })
      });
      const result = await res.json();
      if (result.success) {
        setEnableCondicaoFeedback({ type: 'success', id: codigo });
        setCondicoesList(prev => prev.map(r => r.TB01055_CODIGO === codigo ? { ...r, func_close: 1, update_sla: 0, func_nao_liga: 0, func_sem_pendencia: 0, func_order_st_available_res: 0, func_order_st_unavailable_res: 0 } : r));
        setTimeout(() => setEnableCondicaoFeedback(null), 2000);
      } else {
        const msg = result.message || 'Erro ao habilitar a condição';
        toastError(msg);
        setEnableCondicaoFeedback({ type: 'error', id: codigo, msg });
        setTimeout(() => setEnableCondicaoFeedback(null), 3000);
      }
    } catch (e) {
      setEnableCondicaoFeedback({ type: 'error', id: codigo, msg: 'Erro de conexão' });
      setTimeout(() => setEnableCondicaoFeedback(null), 3000);
    } finally {
      setEnablingCondicaoId(null);
    }
  };

  const startEditCondicaoCampo = (row, field) => {
    setTipoOsOpenCod(null);
    if (field === 'status_peca_disp' && !rowGrupoCamposPreenchidos(row)) return;
    if (CAMPOS_GRUPO_KEYS.includes(field)) {
      setCondicaoEditing({ id: row.TB01055_CODIGO });
      setCondicaoEditValues({
        operacao: String(row.operacao ?? '').trim(),
        condicao: String(row.condicao ?? '').trim(),
        status_disponivel: String(row.status_disponivel ?? '').trim(),
        status_indisponivel: String(row.status_indisponivel ?? '').trim(),
        stock_validation_scope: normalizeStockValidationScope(row.stock_validation_scope),
      });
    } else {
      const val = row[field] ?? '';
      setCondicaoEditing({ id: row.TB01055_CODIGO, field });
      setCondicaoEditValue(String(val).trim());
    }
  };

  const cancelEditCondicaoCampo = () => {
    setCondicaoEditing(null);
    setCondicaoEditValue('');
    setCondicaoEditValues(emptyGrupoValues());
  };

  const updateCondicaoEditGrupo = (key, value) => {
    const cfg = CAMPOS_CONFIG.find(c => c.key === key);
    const capped = sanitizeAlnumCode(value, cfg?.maxLen ?? 10);
    setCondicaoEditValues(prev => ({ ...prev, [key]: capped }));
  };

  const canSaveGrupo = () =>
    CAMPOS_GRUPO_KEYS.every((k) => {
      const cfg = CAMPOS_CONFIG.find((c) => c.key === k);
      return isExactAlnumCode(condicaoEditValues[k], cfg?.maxLen ?? 2);
    });

  const rowGrupoCamposPreenchidos = (row) =>
    CAMPOS_GRUPO_KEYS.every((k) => String(row[k] ?? '').trim() !== '');

  const rowHasStatusRetorno = (row) => String(row.status_retorno ?? '').trim() !== '';

  const rowPodeUsarAtualizaSla = (row) =>
    rowGrupoCamposPreenchidos(row) && rowHasStatusRetorno(row) && String(row.status_peca_disp ?? '').trim() !== '';

  const rowPodeMostrarReservaCheck = (row) =>
    row.func_close === 1 && rowGrupoCamposPreenchidos(row);

  const patchStatusReserve = async (row, kind, checked) => {
    const cod = row.TB01055_CODIGO;
    const fieldKey = kind === 'available' ? 'func_order_st_available_res' : 'func_order_st_unavailable_res';
    const sk = `${cod}-${kind}`;
    const prevVal =
      row[fieldKey] === 1 || row[fieldKey] === true || String(row[fieldKey]) === '1' ? 1 : 0;
    setReserveSavingKey(sk);
    setCondicoesList((prev) =>
      prev.map((r) => (r.TB01055_CODIGO === cod ? { ...r, [fieldKey]: checked ? 1 : 0 } : r))
    );
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/status-reserve`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_condition: cod, field: kind, value: checked })
      });
      const result = await res.json().catch(() => ({}));
      if (!result.success) {
        setCondicoesList((prev) =>
          prev.map((r) => (r.TB01055_CODIGO === cod ? { ...r, [fieldKey]: prevVal } : r))
        );
        setEnableCondicaoFeedback({ type: 'error', id: cod, msg: result.message || 'Erro ao salvar Reservar' });
        setTimeout(() => setEnableCondicaoFeedback(null), 3000);
      }
    } catch {
      setCondicoesList((prev) =>
        prev.map((r) => (r.TB01055_CODIGO === cod ? { ...r, [fieldKey]: prevVal } : r))
      );
      setEnableCondicaoFeedback({ type: 'error', id: cod, msg: 'Erro de conexão' });
      setTimeout(() => setEnableCondicaoFeedback(null), 3000);
    } finally {
      setReserveSavingKey((cur) => (cur === sk ? null : cur));
    }
  };

  /** BIT no SQL/mssql pode vir como true/false no JSON, não só 1/0 */
  const rowUpdateSlaLigado = (row) => {
    const v = row?.update_sla;
    return v === 1 || v === true || String(v) === '1';
  };

  const loadRegBugsIfNeeded = async () => {
    if (regBugsList.length > 0 || regBugsLoading) return;
    setRegBugsLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/reg-bugs`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const result = await res.json();
      if (result.success && Array.isArray(result.data)) setRegBugsList(result.data);
      else setRegBugsList([]);
    } catch {
      setRegBugsList([]);
    } finally {
      setRegBugsLoading(false);
    }
  };

  const openDefeitoPainel = (row) => {
    if (!defeitoFlagLoaded || !defeitoRetornoEnabled) return;
    setTipoOsOpenCod(null);
    setDefeitoExpandId(row.TB01055_CODIGO);
    setDefeitoDraftCodbug(row.codbug != null && String(row.codbug).trim() !== '' ? String(row.codbug).trim() : '');
    setDefeitoDraftReason(sanitizeMotivoDraft(row.codbug_reason ?? ''));
    setDefeitoDraftBlank(row.codbug_blank === 1 || row.codbug_blank === true);
    setDefeitoDraftAsk(row.ask_service_on_new_cycle === 1 || row.ask_service_on_new_cycle === true);
    setDefeitoMotivoHelpOpen(false);
    loadRegBugsIfNeeded();
  };

  const closeDefeitoPainel = () => {
    setDefeitoExpandId(null);
    setDefeitoDraftCodbug('');
    setDefeitoDraftReason('');
    setDefeitoDraftBlank(false);
    setDefeitoDraftAsk(false);
    setDefeitoMotivoHelpOpen(false);
  };

  const onTipoOsToggle = async (row, fieldDef, checked) => {
    const codigo = row.TB01055_CODIGO;
    const val = checked ? 1 : 0;
    const dbKey = fieldDef.db;

    if (rowHasSemPendencia(row)) {
      const nextRow = { ...row, [dbKey]: val };
      const blockMsg = formatSemPendenciaTipoOsBlockMessage(
        getSemPendenciaTipoOsIssues(nextRow),
        { forEnable: false }
      );
      if (blockMsg) {
        toastError(blockMsg);
        return;
      }
    }

    setCondicoesList((prev) =>
      prev.map((r) => (r.TB01055_CODIGO === codigo ? { ...r, [dbKey]: val } : r))
    );
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/tipo-os`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_condition: codigo, field: dbKey, value: val }),
      });
      const result = await res.json().catch(() => ({}));
      if (!result.success) {
        setCondicoesList((prev) =>
          prev.map((r) => (r.TB01055_CODIGO === codigo ? { ...r, [dbKey]: checked ? 0 : 1 } : r))
        );
        toastError(result.message || 'Erro ao atualizar Tipo de OS');
      }
    } catch {
      setCondicoesList((prev) =>
        prev.map((r) => (r.TB01055_CODIGO === codigo ? { ...r, [dbKey]: checked ? 0 : 1 } : r))
      );
      toastError('Erro de conexão');
    }
  };

  const saveDefeitoVinculo = async (row) => {
    if (defeitoExpandId !== row.TB01055_CODIGO) return;
    const reasonTrim = defeitoDraftReason.trim();
    if (reasonTrim.length > 50) {
      setEnableCondicaoFeedback({ type: 'error', id: row.TB01055_CODIGO, msg: 'Motivo: máximo 50 caracteres.' });
      setTimeout(() => setEnableCondicaoFeedback(null), 3000);
      return;
    }
    if (reasonTrim.includes("'") || reasonTrim.includes('/')) {
      setEnableCondicaoFeedback({ type: 'error', id: row.TB01055_CODIGO, msg: 'Motivo não pode conter aspas simples nem barras.' });
      setTimeout(() => setEnableCondicaoFeedback(null), 3000);
      return;
    }
    if (reasonTrim && !/^[A-Za-zÀ-ÖØ-öø-ÿ0-9 ]+$/.test(reasonTrim)) {
      setEnableCondicaoFeedback({ type: 'error', id: row.TB01055_CODIGO, msg: 'Motivo: use apenas letras, números e espaços.' });
      setTimeout(() => setEnableCondicaoFeedback(null), 3000);
      return;
    }
    setDefeitoSavingId(row.TB01055_CODIGO);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/codbug`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_condition: row.TB01055_CODIGO,
          codBug: defeitoDraftCodbug.trim() === '' ? null : defeitoDraftCodbug.trim(),
          codbugReason: reasonTrim === '' ? null : reasonTrim,
          codbugBlank: defeitoDraftBlank,
          ...(nativeMode ? { askServiceOnNewCycle: defeitoDraftAsk } : {}),
        })
      });
      const result = await res.json();
      if (result.success && result.data) {
        const { codbug, codbug_nome, codbug_alert, codbug_reason, codbug_blank, ask_service_on_new_cycle } = result.data;
        setCondicoesList((prev) => prev.map((r) =>
          r.TB01055_CODIGO === row.TB01055_CODIGO
            ? {
              ...r,
              codbug,
              codbug_nome,
              codbug_alert,
              codbug_reason,
              codbug_blank,
              ...(ask_service_on_new_cycle != null ? { ask_service_on_new_cycle } : {}),
            }
            : r
        ));
        closeDefeitoPainel();
        setCondicaoSuccessAnim({ id: row.TB01055_CODIGO, field: 'defeito' });
        setTimeout(() => setCondicaoSuccessAnim(null), 1200);
      } else {
        setEnableCondicaoFeedback({ type: 'error', id: row.TB01055_CODIGO, msg: result.message || 'Erro ao salvar defeito' });
        setTimeout(() => setEnableCondicaoFeedback(null), 3000);
      }
    } catch {
      setEnableCondicaoFeedback({ type: 'error', id: row.TB01055_CODIGO, msg: 'Erro de conexão' });
      setTimeout(() => setEnableCondicaoFeedback(null), 3000);
    } finally {
      setDefeitoSavingId(null);
    }
  };

  const saveCondicaoCampo = async (row) => {
    if (!condicaoEditing || condicaoEditing.id !== row.TB01055_CODIGO) return;
    const { field } = condicaoEditing;
    const isGrupoEdit = !field;
    const statusRetornoVal = isGrupoEdit
      ? (row.status_retorno ?? '')
      : field === 'status_retorno'
        ? (condicaoEditValue ?? '').slice(0, 2).toUpperCase()
        : (row.status_retorno ?? '');
    const statusPecaDispVal = isGrupoEdit
      ? String(row.status_peca_disp ?? '').trim().toUpperCase()
      : field === 'status_peca_disp'
        ? (condicaoEditValue ?? '').trim().toUpperCase()
        : String(row.status_peca_disp ?? '').trim().toUpperCase();
    const singleField = isGrupoEdit
      ? null
      : field === 'status_retorno'
        ? 'retorno'
        : field === 'status_peca_disp'
          ? 'peca_disp'
          : null;
    const payload = {
      id_condition: row.TB01055_CODIGO,
      operacao: isGrupoEdit ? (condicaoEditValues.operacao ?? '').trim() : (row.operacao ?? ''),
      condicao: isGrupoEdit ? (condicaoEditValues.condicao ?? '').trim() : (row.condicao ?? ''),
      statusDisponivel: isGrupoEdit ? (condicaoEditValues.status_disponivel ?? '').trim() : (row.status_disponivel ?? ''),
      statusIndisponivel: isGrupoEdit ? (condicaoEditValues.status_indisponivel ?? '').trim() : (row.status_indisponivel ?? ''),
      statusRetorno: statusRetornoVal,
      statusPecaDisp: statusPecaDispVal,
      stockValidationScope: isGrupoEdit
        ? normalizeStockValidationScope(condicaoEditValues.stock_validation_scope)
        : normalizeStockValidationScope(row.stock_validation_scope),
      singleField
    };
    if (isGrupoEdit && !canSaveGrupo()) return;
    if (!isGrupoEdit && field === 'status_retorno') {
      const v = (condicaoEditValue ?? '').trim();
      if (v.length !== 0 && !isExactAlnumCode(v, 2)) return;
    }
    if (!isGrupoEdit && field === 'status_peca_disp') {
      if (!rowGrupoCamposPreenchidos(row)) return;
      const v = (condicaoEditValue ?? '').trim();
      if (v.length !== 0 && v.length !== 2) return;
    }
    setCondicaoSaving(row.TB01055_CODIGO + (isGrupoEdit ? '_grupo' : '_' + field));
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/campos`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (result.success) {
        if (isGrupoEdit) {
          setCondicoesList(prev => prev.map(r => r.TB01055_CODIGO === row.TB01055_CODIGO
            ? {
                ...r,
                operacao: payload.operacao,
                condicao: payload.condicao,
                status_disponivel: payload.statusDisponivel,
                status_indisponivel: payload.statusIndisponivel,
                stock_validation_scope: payload.stockValidationScope,
              }
            : r));
          setCondicaoEditValues(emptyGrupoValues());
          setCondicaoSuccessAnim({ id: row.TB01055_CODIGO });
        } else {
          const newVal = field === 'status_peca_disp' ? (payload.statusPecaDisp || null) : payload.statusRetorno;
          setCondicoesList(prev => prev.map(r => r.TB01055_CODIGO === row.TB01055_CODIGO ? { ...r, [field]: newVal } : r));
          setCondicaoSuccessAnim({ id: row.TB01055_CODIGO, field });
        }
        setCondicaoEditing(null);
        setCondicaoEditValue('');
        setTimeout(() => setCondicaoSuccessAnim(null), 1200);
      } else {
        toastError(result.message || 'Erro ao salvar');
        setEnableCondicaoFeedback({ type: 'error', id: row.TB01055_CODIGO, msg: result.message || 'Erro ao salvar' });
        setTimeout(() => setEnableCondicaoFeedback(null), 3000);
      }
    } catch (e) {
      toastError('Erro de conexão');
      setEnableCondicaoFeedback({ type: 'error', id: row.TB01055_CODIGO, msg: 'Erro de conexão' });
      setTimeout(() => setEnableCondicaoFeedback(null), 3000);
    } finally {
      setCondicaoSaving(null);
    }
  };

  const desabilitarCondicao = async (codigo) => {
    setDisablingCondicaoId(codigo);
    setDisableCondicaoFeedback(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/desabilitar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ codCondicao: codigo })
      });
      const result = await res.json();
      if (result.success) {
        setCondicoesList(prev => prev.map(r => r.TB01055_CODIGO === codigo
          ? {
              ...r,
              func_close: 0,
              operacao: null,
              condicao: null,
              status_disponivel: null,
              status_indisponivel: null,
              status_retorno: null,
              status_peca_disp: null,
              stock_validation_scope: 'all_companies',
              codbug: null,
              codbug_nome: null,
              codbug_alert: null,
              codbug_reason: null,
              codbug_blank: 0,
              update_sla: 0,
              func_order_st_available_res: 0,
              func_order_st_unavailable_res: 0
            }
          : r));
        setDisableCondicaoFeedback({ type: 'success', id: codigo });
        setDesabilitarOpenId(null);
        setTimeout(() => setDisableCondicaoFeedback(null), 2000);
      } else {
        setDisableCondicaoFeedback({ type: 'error', id: codigo, msg: result.message || 'Erro ao desabilitar' });
        setTimeout(() => setDisableCondicaoFeedback(null), 3000);
      }
    } catch (e) {
      setDisableCondicaoFeedback({ type: 'error', id: codigo, msg: 'Erro de conexão' });
      setTimeout(() => setDisableCondicaoFeedback(null), 3000);
    } finally {
      setDisablingCondicaoId(null);
    }
  };

  const defeitoColInteractive = defeitoFlagLoaded && defeitoRetornoEnabled;

  const displayCondicoesList = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = [...condicoesList];
    if (nativeMode) {
      list = list.filter((row) => (statusTab === 'inativas' ? row.is_active === false : row.is_active !== false));
    }
    if (q) {
      list = list.filter((row) => {
        const haystack = [
          row.TB01055_CODIGO,
          row.TB01055_NOME,
          row.operacao,
          row.condicao,
          row.status_disponivel,
          row.status_indisponivel,
          row.status_retorno,
          row.status_peca_disp,
          row.codbug,
          row.codbug_nome,
          row.codbug_reason,
        ]
          .map((v) => String(v ?? '').toLowerCase())
          .join(' ');
        return haystack.includes(q);
      });
    }
    const dir = sortDir === 'desc' ? -1 : 1;
    list.sort((a, b) => {
      const va = getCondicaoSortValue(a, sortBy).toLowerCase();
      const vb = getCondicaoSortValue(b, sortBy).toLowerCase();
      const na = Number(va);
      const nb = Number(vb);
      if (va !== '' && vb !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) return (na - nb) * dir;
      return va.localeCompare(vb, 'pt-BR', { sensitivity: 'base', numeric: true }) * dir;
    });
    return list;
  }, [condicoesList, searchQuery, sortBy, sortDir, nativeMode, statusTab]);

  const intervTableApi = useMemo(
    () => ({
      CAMPOS_CONFIG,
      CAMPOS_GRUPO_KEYS,
      CAMPOS_INTERV_PREFIX,
      CAMPOS_INTERV_SUFFIX,
      isIntervColVis,
      hasOtherNaoLiga,
      hasOtherSemPendencia,
      hasOtherInstalaEquip,
      hasOtherDesinstalaEquip,
      toastError,
      tipoOsOpenCod,
      setTipoOsOpenCod,
      onTipoOsToggle,
      rowHasStatusRetorno,
      rowPodeUsarAtualizaSla,
      rowUpdateSlaLigado,
      rowPodeMostrarReservaCheck,
      rowGrupoCamposPreenchidos,
      setCondicoesList,
      API_BASE_URL,
      condicaoEditing,
      condicaoEditValue,
      condicaoEditValues,
      condicaoSaving,
      condicaoSuccessAnim,
      updateCondicaoEditGrupo,
      setCondicaoEditValues,
      setCondicaoEditValue,
      startEditCondicaoCampo,
      cancelEditCondicaoCampo,
      saveCondicaoCampo,
      canSaveGrupo,
      reserveSavingKey,
      patchStatusReserve,
      desabilitarOpenId,
      setDesabilitarOpenId,
      desabilitarCondicao,
      disablingCondicaoId,
      disableCondicaoFeedback,
      enablingCondicaoId,
      enableCondicaoFeedback,
      habilitarCondicao,
      colSpanDefeitoExpand,
      defeitoColInteractive,
      defeitoExpandId,
      openDefeitoPainel,
      closeDefeitoPainel,
      defeitoDraftCodbug,
      setDefeitoDraftCodbug,
      defeitoDraftReason,
      setDefeitoDraftReason,
      defeitoDraftBlank,
      setDefeitoDraftBlank,
      defeitoDraftAsk,
      setDefeitoDraftAsk,
      regBugsList,
      regBugsLoading,
      loadRegBugsIfNeeded,
      defeitoSavingId,
      defeitoDdOpenCod,
      setDefeitoDdOpenCod,
      defeitoMotivoHelpOpen,
      setDefeitoMotivoHelpOpen,
      defeitoMotivoHeaderRef,
      saveDefeitoVinculo,
      hasGo,
      nativeMode,
      erpTransition,
      openEditCondition,
    }),
    [
      hasGo,
      nativeMode,
      erpTransition,
      openEditCondition,
      isIntervColVis,
      hasOtherNaoLiga,
      hasOtherSemPendencia,
      hasOtherInstalaEquip,
      hasOtherDesinstalaEquip,
      toastError,
      tipoOsOpenCod,
      onTipoOsToggle,
      rowHasStatusRetorno,
      rowPodeUsarAtualizaSla,
      rowUpdateSlaLigado,
      rowPodeMostrarReservaCheck,
      rowGrupoCamposPreenchidos,
      condicaoEditing,
      condicaoEditValue,
      condicaoEditValues,
      condicaoSaving,
      condicaoSuccessAnim,
      updateCondicaoEditGrupo,
      setCondicaoEditValues,
      startEditCondicaoCampo,
      cancelEditCondicaoCampo,
      saveCondicaoCampo,
      canSaveGrupo,
      reserveSavingKey,
      patchStatusReserve,
      desabilitarOpenId,
      disablingCondicaoId,
      disableCondicaoFeedback,
      enablingCondicaoId,
      enableCondicaoFeedback,
      desabilitarCondicao,
      habilitarCondicao,
      colSpanDefeitoExpand,
      defeitoColInteractive,
      defeitoExpandId,
      openDefeitoPainel,
      closeDefeitoPainel,
      defeitoDraftCodbug,
      defeitoDraftReason,
      defeitoDraftBlank,
      defeitoDraftAsk,
      regBugsList,
      regBugsLoading,
      loadRegBugsIfNeeded,
      defeitoSavingId,
      defeitoDdOpenCod,
      defeitoMotivoHelpOpen,
      saveDefeitoVinculo,
    ]
  );

  return (
    <div className="pcs2-page condicoes-interv-page">
      <Pcs2Section
        className="condicoes-interv-section-table"
        icon={Tags}
        title="Configuração de condições"
        desc="Configure quais condições de intervenção permitem fechar ordem de serviço, e suas automações."
      >
        <Pcs2Subblock
          title="Condições de intervenção"
          desc="Defina status, automações e permissões de fechamento por condição."
          action={
            <CondicoesIntervToolbar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              sortDir={sortDir}
              onSortChange={setSortBy}
              onSortDirChange={setSortDir}
              isIntervColVis={isIntervColVis}
              onToggleCol={(k) => {
                setColIntervVisibility((prev) => ({ ...prev, [k]: prev[k] === false ? true : false }));
              }}
              onRestoreCols={() => setColIntervVisibility(fullVisibilityState())}
            >
              {nativeMode ? (
                <span className="dv2-native-actions">
                  <div className="go-dev-status-tabs" role="tablist" aria-label="Filtrar condições por situação">
                    {[
                      ['ativas', 'Ativas'],
                      ['inativas', 'Inativas'],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={statusTab === key}
                        className={`go-dev-status-tab${statusTab === key ? ' is-active' : ''}`}
                        onClick={() => setStatusTab(key)}
                      >
                        <span>{label}</span>
                        <span className="go-dev-status-tab-count">
                          {condicoesList.filter((row) => (key === 'inativas' ? row.is_active === false : row.is_active !== false)).length}
                        </span>
                      </button>
                    ))}
                  </div>
                  <GoHoverTooltip text={conditionForm ? '' : 'Nova condição'} align="center" zIndex={12000}>
                    <button
                      type="button"
                      className="equipe2-add-btn equipe2-add-btn--attention"
                      aria-label="Nova condição"
                      onClick={() => setConditionForm({ mode: 'create', codigo: null })}
                    >
                      <span className="equipe2-add-btn-plus">+</span>
                    </button>
                  </GoHoverTooltip>
                </span>
              ) : null}
            </CondicoesIntervToolbar>
          }
        >
          {isLoadingCondicoes ? (
            <ProcessConfigSkeleton variant="tableBody" ariaLabel="Carregando condições" />
          ) : (
            <div className="condicoes-interv-table-wrap">
              <CondicoesIntervencaoTableV2
                rows={displayCondicoesList}
                totalRows={condicoesList.length}
                api={intervTableApi}
                pecaDispHelpOpen={pecaDispHelpOpen}
                setPecaDispHelpOpen={setPecaDispHelpOpen}
                pecaDispHeaderRef={pecaDispHeaderRef}
                atualizaSlaHelpOpen={atualizaSlaHelpOpen}
                setAtualizaSlaHelpOpen={setAtualizaSlaHelpOpen}
                atualizaSlaHeaderRef={atualizaSlaHeaderRef}
                installHelpOpen={installHelpOpen}
                setInstallHelpOpen={setInstallHelpOpen}
                installHeaderRef={installHeaderRef}
                uninstallHelpOpen={uninstallHelpOpen}
                setUninstallHelpOpen={setUninstallHelpOpen}
                uninstallHeaderRef={uninstallHeaderRef}
                naoLigaHelpOpen={naoLigaHelpOpen}
                setNaoLigaHelpOpen={setNaoLigaHelpOpen}
                naoLigaHeaderRef={naoLigaHeaderRef}
                semPendenciaHelpOpen={semPendenciaHelpOpen}
                setSemPendenciaHelpOpen={setSemPendenciaHelpOpen}
                semPendenciaHeaderRef={semPendenciaHeaderRef}
                tipoOsHelpOpen={tipoOsHelpOpen}
                setTipoOsHelpOpen={setTipoOsHelpOpen}
                tipoOsHeaderRef={tipoOsHeaderRef}
              />
            </div>
          )}
        </Pcs2Subblock>
      </Pcs2Section>

      {nativeMode ? (
        <>
          <hr className="pcs2-divider" />
          <Pcs2Section
            icon={UserCog}
            title="Técnico âncora para pedidos"
            desc="Técnico do ERP usado no pedido gerado no fechamento quando o técnico da OS existe só no Vervify."
          >
            <Pcs2Subblock hideHead>
              <div className="pcs2-card pcs2-card--flat">
                <Pcs2SettingRow
                  title="Código do técnico"
                  desc={
                    anchorTech.saved && !anchorDirty
                      ? `${anchorTech.saved} · ${anchorTech.name || 'não encontrado no ERP'}`
                      : '4 dígitos; precisa existir e estar ativo no ERP.'
                  }
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    className="pcs2-list-pin ci-anchor-input"
                    maxLength={4}
                    placeholder="0000"
                    value={anchorTech.value}
                    disabled={!anchorTech.loaded || anchorSaving}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setAnchorTech((prev) => ({ ...prev, value }));
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveAnchorTech(); }}
                    aria-label="Código do técnico âncora"
                  />
                  <button
                    type="button"
                    className="ci-anchor-save"
                    disabled={!anchorDirty || !anchorValid || anchorSaving}
                    onClick={saveAnchorTech}
                  >
                    {anchorSaving ? 'Salvando...' : 'Salvar'}
                  </button>
                </Pcs2SettingRow>
              </div>
            </Pcs2Subblock>
          </Pcs2Section>
        </>
      ) : null}

      <hr className="pcs2-divider" />

      <Pcs2Section
        icon={IconMessageReply}
        tablerIcon
        title={nativeMode ? 'Serviços em retorno técnico' : 'Defeito em retorno técnico'}
        desc={nativeMode
          ? 'Ao habilitar, é possível vincular um serviço na ordem de serviço de retorno.'
          : 'Ao habilitar, é possível vincular um defeito na ordem de serviço de retorno.'}
      >
        <Pcs2Subblock hideHead>
          <div className="pcs2-card pcs2-card--flat">
            <Pcs2SettingRow
              title={nativeMode ? 'Habilitar vinculação de serviço' : 'Habilitar vinculação de defeito'}
              desc={nativeMode
                ? 'Permite configurar serviço e motivo padrão nas condições de retorno técnico.'
                : 'Permite configurar defeito e motivo padrão nas condições de retorno técnico.'}
              descInfo={nativeMode ? SERVICO_RETORNO_INFO : DEFEITO_RETORNO_INFO}
            >
              <Pcs2Switch
                checked={defeitoRetornoEnabled}
                disabled={!defeitoFlagLoaded || defeitoFlagSaving}
                onChange={(e) => toggleDefeitoRetornoFlag(e.target.checked)}
                ariaLabel={nativeMode ? 'Serviços em retorno técnico' : 'Defeito em retorno técnico'}
              />
            </Pcs2SettingRow>
          </div>
        </Pcs2Subblock>
      </Pcs2Section>

      <CondicoesIntervNativeConditionModal
        open={!!conditionForm}
        mode={conditionForm?.mode}
        codigo={conditionForm?.codigo}
        row={condicoesList.find((r) => String(r.TB01055_CODIGO) === String(conditionForm?.codigo)) || null}
        api={intervTableApi}
        nativeMode={nativeMode}
        baseUrl={API_BASE_URL}
        erpTransition={erpTransition}
        onClose={() => setConditionForm(null)}
        onSaved={loadCondicoesIntervencao}
      />

      {Toaster}
    </div>
  );
}
